import { CanonicalSearchLoad, MORE_LOADS_PAGE_SIZE, paginateReplyLoads } from '@numeo/types'
import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { IconButton } from 'components/ds'
import { DispatchLoadCard } from './DispatchLoadCard'

/**
 * The load cards one `show_loads` turn put on the driver's screen, paged exactly as the phone pages
 * them: the agent's top picks alone on the first page, then the second tier MORE_LOADS_PAGE_SIZE at
 * a time behind a `←  Showing N of M loads  →` control.
 *
 * The point is fidelity, not saving space. Stacking every card — what this thread did before —
 * turned a reply the driver experienced as five picks into a flat column of up to seventy cards,
 * with nothing to say which ones they actually had in front of them when they answered. An operator
 * reading back a conversation has to be able to tell a pick from an also-ran.
 *
 * `pickCount` is where the picks end in `loads`. Without it (a server that predates the field) there
 * is no boundary to page at, so the cards render flat — what the thread always did, never less.
 *
 * The page math is `@numeo/types`' `paginateReplyLoads` — the same function the phone calls — so the
 * two surfaces can't drift into disagreeing about where a reply's first page ends.
 */
export function ThreadLoads({
    loads,
    pickCount,
    selectedIds,
    onToggle,
}: {
    loads: CanonicalSearchLoad[]
    pickCount?: number
    selectedIds: Set<string>
    onToggle: (id: string) => void
}) {
    const boundary = pickCount ?? loads.length
    const picks = loads.slice(0, boundary)
    const extras = loads.slice(boundary)
    const [page, setPage] = useState(0)
    const paging = useMemo(
        () => paginateReplyLoads({ pickCount: picks.length, moreCount: extras.length, page, pageSize: MORE_LOADS_PAGE_SIZE }),
        [picks.length, extras.length, page]
    )
    // A refetch can hand back a SHORTER tier than the page the operator is standing on — hydration
    // drops rows archived since the last fetch. paginateReplyLoads clamps that; writing the clamp back
    // keeps the arrows acting on the page actually shown, and stops a later, longer tier from silently
    // teleporting the reply off its picks page.
    useEffect(() => {
        if (paging.page !== page) setPage(paging.page)
    }, [paging.page, page])

    const shown = paging.onPicksPage ? picks : extras.slice(paging.moreStart, paging.moreEnd)
    const range = paging.rangeStart === paging.rangeEnd ? String(paging.rangeStart) : `${paging.rangeStart}-${paging.rangeEnd}`
    const atStart = paging.page <= 0
    const atEnd = paging.page >= paging.pageCount - 1

    return (
        <div className="space-y-1.5">
            {shown.map((l) => (
                <DispatchLoadCard key={l.id} load={l} selected={selectedIds.has(l.id)} onToggle={onToggle} />
            ))}
            {/* Only when the tier actually spans pages — on a single-page reply the control would be two
                dead arrows over a count the cards already show. Same condition and same copy as the phone. */}
            {paging.pageCount > 1 && (
                <div className="flex items-center justify-between gap-2 pt-0.5">
                    <IconButton
                        variant="subtle"
                        size="md"
                        className="rounded-full"
                        icon={<ArrowLeft className="h-3.5 w-3.5" />}
                        disabled={atStart}
                        onClick={() => setPage(paging.page - 1)}
                        aria-label="Previous loads"
                    />
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        Showing {range} of {loads.length} load{loads.length === 1 ? '' : 's'}
                    </span>
                    <IconButton
                        variant="subtle"
                        size="md"
                        className="rounded-full"
                        icon={<ArrowRight className="h-3.5 w-3.5" />}
                        disabled={atEnd}
                        onClick={() => setPage(paging.page + 1)}
                        aria-label="Next loads"
                    />
                </div>
            )}
        </div>
    )
}
